/**
 * gettingStartedStore — completion state for the floating Getting Started panel.
 *
 * Populated from GET /api/getting-started/status on load, then updated
 * optimistically by useGettingStartedListener after each successful claim.
 * The backend remains the idempotency source of truth — this store only
 * drives the UI (task list + avoiding redundant claim calls).
 *
 * `panelOpen` is a session-only UI flag (NOT persisted): closing the panel via
 * its "X" only hides it for the current page load — it pops back up on the
 * next reload as a nudge, until every task is completed. `dismissed` is the
 * only persisted flag, and is set exactly once, automatically, after the
 * user has completed all tasks (see GettingStartedPanel's auto-dismiss
 * effect) — from then on the panel never shows again.
 */
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import fetchService from '../services/fetchService';
import { API_BASE_URL } from '../api/config';
import { createFileLogger } from '../utils/logger';

const log = createFileLogger('GETTING_STARTED_STORE');

export type GettingStartedTaskType =
  | 'create_first_doc'
  | 'create_first_folder'
  | 'connect_first_integration'
  | 'upgrade_plan';

export interface GettingStartedTaskState {
  completed: boolean;
  amountUsd: number;
}

interface GettingStartedStore {
  tasks: Record<GettingStartedTaskType, GettingStartedTaskState>;
  loading: boolean;
  loaded: boolean;
  /** Session-only visibility — reset to `true` on every fresh page load. */
  panelOpen: boolean;
  /** Persisted forever once all tasks are completed. */
  dismissed: boolean;
  fetchStatus: () => Promise<void>;
  markCompleted: (taskType: GettingStartedTaskType, amountUsd: number) => void;
  setPanelOpen: (open: boolean) => void;
  dismissPanel: () => void;
}

const DEFAULT_TASKS: Record<GettingStartedTaskType, GettingStartedTaskState> = {
  create_first_doc: { completed: false, amountUsd: 0.5 },
  create_first_folder: { completed: false, amountUsd: 0.5 },
  connect_first_integration: { completed: false, amountUsd: 1.5 },
  upgrade_plan: { completed: false, amountUsd: 5.0 },
};

export const useGettingStartedStore = create<GettingStartedStore>()(
  persist(
    (set, get) => ({
      tasks: DEFAULT_TASKS,
      loading: false,
      loaded: false,
      panelOpen: true,
      dismissed: false,

      fetchStatus: async () => {
        if (get().loading) return;
        set({ loading: true });
        try {
          const data: { tasks: Record<GettingStartedTaskType, { completed: boolean; amount_usd: number }> } =
            await fetchService(`${API_BASE_URL}/getting-started/status`);
          const tasks = { ...DEFAULT_TASKS };
          (Object.keys(data.tasks) as GettingStartedTaskType[]).forEach((taskType) => {
            tasks[taskType] = {
              completed: data.tasks[taskType].completed,
              amountUsd: data.tasks[taskType].amount_usd,
            };
          });
          set({ tasks, loading: false, loaded: true });
          log.log('[GettingStartedStore] status loaded', tasks);
        } catch (err) {
          log.error('[GettingStartedStore] failed to load status', err);
          set({ loading: false });
          // Callers gate re-fetching on `loaded` flipping to true, so a transient
          // failure (e.g. a request that races the backend coming up) would
          // otherwise never be retried for the rest of the session. One backoff
          // retry mirrors the plan-upgrade backstop pattern in
          // useGettingStartedListener.ts.
          window.setTimeout(() => {
            if (!get().loaded) get().fetchStatus();
          }, 3000);
        }
      },

      markCompleted: (taskType, amountUsd) => {
        set((state) => ({
          tasks: {
            ...state.tasks,
            [taskType]: { completed: true, amountUsd },
          },
        }));
      },

      setPanelOpen: (open) => set({ panelOpen: open }),
      dismissPanel: () => set({ panelOpen: false, dismissed: true }),
    }),
    {
      name: 'getting-started-panel-storage',
      storage: createJSONStorage(() => localStorage),
      // Only `dismissed` persists — `panelOpen` always starts `true` on a fresh load.
      partialize: (state) => ({ dismissed: state.dismissed }),
    },
  ),
);
